import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import ReactQuill from 'react-quill';
import styled from 'styled-components';
import { useProcContext } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import { useUpdateComponent } from '@/shared/api';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { captureError } from '@/shared/utils/error-reporting';

const toolbar = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ size: [] }, { align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }, 'link'],
];
const formats = [
  'background',
  'bold',
  'color',
  'font',
  'code',
  'italic',
  'link',
  'size',
  'strike',
  'script',
  'underline',
  'blockquote',
  'indent',
  'list',
  'align',
  'direction',
  'code-block',
  'formula',
  'image',
  'video',
];

// Clean HTML output from Quill
const cleanHtml = (html: string): string => {
  return (
    html
      // Remove extra spaces
      .replace(/\s+/g, ' ')
      // Remove empty paragraphs
      .replace(/<p><\/p>/g, '')
      // Clean up newlines
      .replace(/\n/g, '')
      // Clean up any double spaces
      .trim()
  );
};

export const TextEditForm = forwardRef((_, ref) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { theme } = useThemeContext();
  const editorRef = useRef<any>(null);
  const { selectedComponent, selectedStep, proc, setSelectedComponent } =
    useProcContext();
  const [form] = Form.useForm();

  const { mutate: updateComponent, isLoading: updateComponentLoading } =
    useUpdateComponent();

  const [content, setContent] = useState<string>(() => {
    return selectedComponent?.value || '';
  });

  useImperativeHandle(
    ref,
    () => ({
      submit: () => form.submit(),
      isLoading: updateComponentLoading,
    }),
    [updateComponentLoading, selectedComponent?.id, selectedStep?.id, form],
  );

  const onSubmit = async () => {
    try {
      await form.validateFields();

      updateComponent(
        {
          pathParams: { procId: proc?.id!, stepId: selectedStep?.id! },
          body: {
            id: selectedComponent?.id!,
            value: cleanHtml(content),
            component: selectedComponent?.component,
            componentName: selectedComponent?.componentName,
          },
        },
        {
          onSuccess: () => {
            setSelectedComponent(undefined);
            queryClient.invalidateQueries({
              queryKey: ['procs', proc?.id],
            });
          },
          onError: (error: any) => {
            showNotification({
              type: 'error',
              message: 'Update error',
              description: error.stack.message,
            });
            captureError(error, {
              module: 'text_edit_form_on_error_update_component',
              action: 'update_component',
            });
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'text_edit_form_on_error_update_component',
        action: 'update_component',
      });
    }
  };

  const handleEditorChange = (html: string) => {
    setContent(html);
    form.setFieldsValue({ text: html });
  };

  useEffect(() => {
    editorRef.current?.focus();
  }, []);

  useEffect(() => {
    if (selectedComponent?.value) {
      setContent(selectedComponent.value);
      form.setFieldsValue({ text: selectedComponent.value });
    } else {
      setContent('');
      form.setFieldsValue({ text: '' });
    }
  }, [selectedComponent]);

  return (
    <Form
      data-testid="text-edit-form"
      form={form}
      name="textEditForm"
      initialValues={{
        text: content,
      }}
      onFinish={onSubmit}
      autoComplete="off"
      layout="vertical"
      style={{ width: '100%' }}
    >
      <Form.Item
        label="Field value"
        name="text"
        style={{ width: '100%' }}
        rules={[
          {
            required: true,
            message: 'Field value is required',
          },
        ]}
      >
        <QuillEditor
          ref={editorRef}
          modules={{ toolbar }}
          formats={formats}
          value={content}
          onChange={handleEditorChange}
          placeholder="Add text here..."
          $toolbarBgColor={themeConfig[theme].colors.contentBg}
          $scrollbarThumbColor={themeConfig[theme].colors.contentBgSecondary}
          $color={themeConfig[theme].colors.text}
        />
      </Form.Item>
    </Form>
  );
});

const QuillEditor = styled(ReactQuill)<{
  $toolbarBgColor: string;
  $color: string;
  $scrollbarThumbColor: string;
}>`
  .ql-container.ql-snow {
    height: 500px !important;
    max-height: 50vh !important;
  }
  .ql-toolbar.ql-snow {
    background-color: ${({ $toolbarBgColor }) => $toolbarBgColor} !important;
  }
  p,
  span,
  strong {
    color: ${({ $color }) => $color} !important;
  }
  .ql-picker-options {
    background-color: ${({ $toolbarBgColor }) => $toolbarBgColor} !important;
  }
  .ql-editor {
    ::-webkit-scrollbar {
      width: 14px;
    }
    ::-webkit-scrollbar-thumb {
      background-color: ${({ $scrollbarThumbColor }) => $scrollbarThumbColor};
      border: 4px solid transparent;
      border-radius: 8px;
      background-clip: padding-box;
    }
  }
`;
